import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { format, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cpApi } from '../api/api';
import type { VNDPayoutRequest, VNDTierRateRow } from '../types';
import PayoutCompleteConfirmWidget from '../components/PayoutCompleteConfirmWidget';
import PayoutTakeConfirmWidget from '../components/PayoutTakeConfirmWidget';
import HoverTooltip from '../components/ui/HoverTooltip';
import VndPageLoading from '../components/VndPageLoading';
import { useVndRequestsFilters } from '../context/VndRequestsFiltersContext';
import { getAutoRefreshInterval, isAutoRefreshEnabled } from '../utils/preferences';

const PAGE_SIZE = 12;

function asObj(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function bankField(b: unknown, key: 'bank' | 'bank_holder' | 'bank_number' | 'qr'): string {
  const obj = asObj(b);
  const value = obj[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : '';
}

const statusClass: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  work: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  pending_canceled: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const statusCardToneClass: Record<string, string> = {
  open: 'border-blue-200/70 bg-blue-50/30 dark:border-blue-900/50 dark:bg-blue-950/15',
  work: 'border-amber-200/70 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/15',
  in_progress: 'border-amber-200/70 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/15',
  closed: 'border-emerald-200/70 bg-emerald-50/30 dark:border-emerald-900/50 dark:bg-emerald-950/15',
  canceled: 'border-rose-200/70 bg-rose-50/30 dark:border-rose-900/50 dark:bg-rose-950/15',
  pending_canceled: 'border-purple-200/70 bg-purple-50/30 dark:border-purple-900/50 dark:bg-purple-950/15',
};

const STATUS_I18N_KEYS: Record<
  string,
  'status_open' | 'status_work' | 'status_in_progress' | 'status_closed' | 'status_canceled' | 'status_pending_canceled'
> = {
  open: 'status_open',
  work: 'status_work',
  in_progress: 'status_in_progress',
  closed: 'status_closed',
  canceled: 'status_canceled',
  pending_canceled: 'status_pending_canceled',
};

function formatCreatedDateTime(date: string, na: string): string {
  if (!date) return na;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return na;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function metaNumber(meta: Record<string, unknown> | undefined, key: string): number | null {
  if (!meta) return null;
  const raw = meta[key];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw.trim().replace(',', '.'));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function pickPayoutRatePercentByAmount(tiers: VNDTierRateRow[], amountVnd: number): number {
  for (const tier of tiers) {
    const min = Number(tier.min_vnd || 0);
    const max = Number(tier.max_vnd || 0);
    const pct = Number(tier.rate_percent || 0);
    if (!Number.isFinite(pct)) continue;
    const minOk = amountVnd >= min;
    const maxOk = max <= 0 || amountVnd <= max;
    if (minOk && maxOk) return pct;
  }
  return 0;
}

function formatRate2(value: number): string {
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatUSDT2(value: number): string {
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolvePayoutCounterpartyRate(baseRate: number, ratePercent: number): number {
  if (!(baseRate > 0)) return 0;
  const effective = baseRate * (1 - ratePercent / 100);
  return effective > 0 ? effective : baseRate;
}

function formatElapsed(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const minutes = Math.floor(sec / 60);
  if (minutes < 60) {
    const seconds = sec % 60;
    return `${pad2(minutes)} мин ${pad2(seconds)} сек`;
  }
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${pad2(hours)} ч ${pad2(remainingMinutes)} мин`;
  }
  const days = Math.floor(minutes / 1440);
  const remainingHours = Math.floor((minutes % 1440) / 60);
  return `${pad2(days)} д ${pad2(remainingHours)} ч`;
}

function payoutWorkTimeColor(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 30) return 'text-green-600 dark:text-green-400';
  if (minutes < 60) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function payoutWorkTime(p: VNDPayoutRequest, nowMs: number): { text: string; color: string } {
  const start = Date.parse(p.created_at || '');
  if (!Number.isFinite(start)) return { text: '—', color: 'text-gray-400 dark:text-gray-500' };
  const inProgress = p.status === 'pending' || p.status === 'in_progress' || p.status === 'problem';
  const endRaw = inProgress ? nowMs : Date.parse(p.updated_at || '');
  const end = Number.isFinite(endRaw) ? endRaw : nowMs;
  const delta = Math.max(0, end - start);
  return { text: formatElapsed(delta), color: payoutWorkTimeColor(delta) };
}

function payoutCardUiStatus(p: VNDPayoutRequest): string {
  const isActive = p.status === 'pending' || p.status === 'in_progress' || p.status === 'problem';
  const assignedToMe = typeof p.status_actor_id === 'number' && p.status_actor_id > 0;
  const isOpenPool = isActive && !assignedToMe;
  if (isOpenPool) return 'open';
  if (p.status === 'completed') return 'closed';
  if (p.status === 'canceled') return 'canceled';
  if (p.status === 'in_progress' || p.status === 'pending' || p.status === 'problem') return 'work';
  return p.status || 'other';
}

function payoutStatusRank(uiStatus: string): number {
  if (uiStatus === 'open') return 0;
  if (uiStatus === 'work') return 1;
  if (uiStatus === 'closed') return 2;
  if (uiStatus === 'canceled') return 3;
  return 4;
}

export default function VndPayoutsPage() {
  const { t } = useTranslation();
  const { statusFilter, dateRange, searchQuery } = useVndRequestsFilters();
  const [rows, setRows] = useState<VNDPayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyID, setBusyID] = useState<number | null>(null);
  const [confirmID, setConfirmID] = useState<number | null>(null);
  const [takeConfirmID, setTakeConfirmID] = useState<number | null>(null);
  const [qrModalID, setQrModalID] = useState<number | null>(null);
  const [qrPreviewLoading, setQrPreviewLoading] = useState(false);
  const [qrPreviewLoadFailed, setQrPreviewLoadFailed] = useState(false);
  const [qrPreviewImageUrl, setQrPreviewImageUrl] = useState<string | null>(null);
  const qrPreviewObjectUrlRef = useRef<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [payoutTiers, setPayoutTiers] = useState<VNDTierRateRow[]>([]);
  const [payoutTiersLoaded, setPayoutTiersLoaded] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => isAutoRefreshEnabled());
  const [autoRefreshIntervalSec, setAutoRefreshIntervalSec] = useState(() => getAutoRefreshInterval());
  const [lockedPayoutIds, setLockedPayoutIds] = useState<Set<number>>(new Set());

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const tm = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(tm);
  }, [searchQuery]);

  const dateFrom = useMemo(
    () => (dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd') : ''),
    [dateRange.from]
  );
  const dateTo = useMemo(
    () => (dateRange.to ? format(startOfDay(dateRange.to), 'yyyy-MM-dd') : ''),
    [dateRange.to]
  );

  useLayoutEffect(() => {
    setPage(1);
  }, [statusFilter, dateFrom, dateTo, debouncedSearch]);

  const apiStatus = useMemo(() => (statusFilter !== 'all' ? statusFilter : undefined), [statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cpApi.getVndPayoutRequests({
        status: apiStatus,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: debouncedSearch.trim() || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(res.items);
      setTotalPages(Math.max(1, res.total_pages));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки выплат');
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [apiStatus, dateFrom, dateTo, debouncedSearch, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => void load();
    window.addEventListener('refreshRequests', onRefresh);
    return () => window.removeEventListener('refreshRequests', onRefresh);
  }, [load]);

  useEffect(() => {
    const onAutoRefreshChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled?: boolean; interval?: number }>).detail;
      if (typeof detail?.enabled === 'boolean') {
        setAutoRefreshEnabled(detail.enabled);
      }
      if (typeof detail?.interval === 'number' && Number.isFinite(detail.interval)) {
        setAutoRefreshIntervalSec(Math.max(60, Math.min(300, Math.round(detail.interval))));
      }
    };
    window.addEventListener('autoRefreshChanged', onAutoRefreshChanged);
    return () => window.removeEventListener('autoRefreshChanged', onAutoRefreshChanged);
  }, []);

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const id = window.setInterval(() => {
      void load();
    }, autoRefreshIntervalSec * 1000);
    return () => window.clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshIntervalSec, load]);

  useEffect(() => {
    let cancelled = false;
    const loadTiers = async () => {
      try {
        const res = await cpApi.getVndTeamPayoutTiers();
        if (!cancelled) {
          setPayoutTiers((res.tiers || []) as VNDTierRateRow[]);
          setPayoutTiersLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPayoutTiers([]);
          setPayoutTiersLoaded(true);
        }
      }
    };
    void loadTiers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    window.dispatchEvent(
      new CustomEvent('paginationUpdate', {
        detail: { currentPage: page, totalPages, showPagination: true },
      })
    );
  }, [page, totalPages, loading]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onPageChange = (e: Event) => {
      const p = (e as CustomEvent<{ page: number }>).detail?.page;
      if (typeof p === 'number' && p >= 1) setPage(p);
    };
    window.addEventListener('pageChange', onPageChange);
    return () => window.removeEventListener('pageChange', onPageChange);
  }, []);

  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent('paginationUpdate', {
          detail: { currentPage: 1, totalPages: 1, showPagination: false },
        })
      );
    };
  }, []);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const ra = payoutStatusRank(payoutCardUiStatus(a));
        const rb = payoutStatusRank(payoutCardUiStatus(b));
        if (ra !== rb) return ra - rb;
        return b.id - a.id;
      }),
    [rows]
  );
  const current = useMemo(() => rows.find((x) => x.id === confirmID) ?? null, [rows, confirmID]);
  const takeCurrent = useMemo(() => rows.find((x) => x.id === takeConfirmID) ?? null, [rows, takeConfirmID]);
  const qrModalPayout = useMemo(() => rows.find((x) => x.id === qrModalID) ?? null, [rows, qrModalID]);
  const qrModalValue = useMemo(() => (qrModalPayout ? bankField(qrModalPayout.bank_details, 'qr').trim() : ''), [qrModalPayout]);

  useEffect(() => {
    let cancelled = false;
    const loadQrPreviewImage = async () => {
      if (!qrModalPayout || !qrModalValue) return;
      const isImageLike = qrModalValue.startsWith('data:image/') || /^https?:\/\//i.test(qrModalValue);
      if (!isImageLike) return;
      setQrPreviewLoading(true);
      setQrPreviewLoadFailed(false);
      try {
        const token = localStorage.getItem('airex_cp_token');
        const headers = new Headers();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        const resp = await fetch(`/api/counterparty/vnd-payout-requests/${qrModalPayout.id}/qr-image`, {
          method: 'GET',
          headers,
        });
        if (!resp.ok) throw new Error('failed');
        const blob = await resp.blob();
        if (cancelled) return;
        if (qrPreviewObjectUrlRef.current) URL.revokeObjectURL(qrPreviewObjectUrlRef.current);
        const objectUrl = URL.createObjectURL(blob);
        qrPreviewObjectUrlRef.current = objectUrl;
        setQrPreviewImageUrl(objectUrl);
        setQrPreviewLoading(false);
        setQrPreviewLoadFailed(false);
      } catch {
        if (!cancelled) {
          setQrPreviewImageUrl(null);
          setQrPreviewLoading(false);
          setQrPreviewLoadFailed(true);
        }
      }
    };
    setQrPreviewImageUrl(null);
    setQrPreviewLoadFailed(false);
    setQrPreviewLoading(false);
    void loadQrPreviewImage();
    return () => {
      cancelled = true;
    };
  }, [qrModalPayout, qrModalValue]);

  useEffect(() => {
    return () => {
      if (qrPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(qrPreviewObjectUrlRef.current);
        qrPreviewObjectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (qrModalID != null) return;
    if (qrPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(qrPreviewObjectUrlRef.current);
      qrPreviewObjectUrlRef.current = null;
    }
    setQrPreviewImageUrl(null);
    setQrPreviewLoading(false);
    setQrPreviewLoadFailed(false);
  }, [qrModalID]);

  const takePayout = useCallback(async (id: number) => {
    if (lockedPayoutIds.has(id)) return;
    setLockedPayoutIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setBusyID(id);
    setError(null);
    try {
      await cpApi.takeVndPayoutRequest(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Ошибка взятия в работу #${id}`);
    } finally {
      setBusyID(null);
      setLockedPayoutIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [load, lockedPayoutIds]);

  const completeConfirmed = useCallback(async () => {
    if (!confirmID) return;
    if (lockedPayoutIds.has(confirmID)) return;
    setLockedPayoutIds((prev) => {
      const next = new Set(prev);
      next.add(confirmID);
      return next;
    });
    setBusyID(confirmID);
    setError(null);
    try {
      await cpApi.completeVndPayoutRequest(confirmID);
      setConfirmID(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Ошибка подтверждения #${confirmID}`);
    } finally {
      setBusyID(null);
      setLockedPayoutIds((prev) => {
        const next = new Set(prev);
        next.delete(confirmID);
        return next;
      });
    }
  }, [confirmID, load, lockedPayoutIds]);

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4">
      {error ? <div className="text-sm text-red-500">{error}</div> : null}
      <div className="overflow-hidden rounded-xl border border-slate-200/70 bg-white shadow-sm dark:border-gray-700/70 dark:bg-gray-800/90">
        {loading ? (
          <VndPageLoading fillTable />
        ) : sorted.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500 dark:text-gray-400">{t('common.notAvailable')}</div>
        ) : (
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {sorted.map((p) => {
                const bank = bankField(p.bank_details, 'bank');
                const holder = bankField(p.bank_details, 'bank_holder');
                const number = bankField(p.bank_details, 'bank_number');
                const qr = bankField(p.bank_details, 'qr');
                const bankDetailsEntries = [
                  { label: 'Банк', value: bank, mono: false },
                  { label: 'Получатель', value: holder, mono: false },
                  { label: 'Счёт', value: number, mono: true },
                ].filter((x) => x.value);
                const requestRate = metaNumber(p.meta, 'vnd_per_usdt');
                const fixedRewardPercent = metaNumber(p.meta, 'cp_reward_percent');
                const tierPercent = payoutTiersLoaded
                  ? pickPayoutRatePercentByAmount(payoutTiers, Number(p.amount_vnd || 0))
                  : null;
                const appliedPercent =
                  fixedRewardPercent != null && fixedRewardPercent > 0 ? fixedRewardPercent : tierPercent;
                const counterpartyRate =
                  requestRate != null && requestRate > 0 && appliedPercent != null
                    ? resolvePayoutCounterpartyRate(requestRate, appliedPercent)
                    : null;
                const amountUSDT =
                  counterpartyRate != null && counterpartyRate > 0
                    ? Number(p.amount_vnd || 0) / counterpartyRate
                    : null;
                const amountUSDTFullTooltip =
                  amountUSDT != null && amountUSDT > 0 ? `${amountUSDT} USDT` : '—';
                const isActive = p.status === 'pending' || p.status === 'in_progress' || p.status === 'problem';
                const assignedToMe = typeof p.status_actor_id === 'number' && p.status_actor_id > 0;
                const isOpenPool = isActive && !assignedToMe;
                const wt = payoutWorkTime(p, nowMs);
                const uiStatus =
                  isOpenPool
                    ? 'open'
                    : p.status === 'completed'
                      ? 'closed'
                      : p.status === 'problem'
                        ? 'pending_canceled'
                        : p.status === 'in_progress'
                          ? 'work'
                          : p.status;
                const statusLabel = STATUS_I18N_KEYS[uiStatus] ? t(`requests.${STATUS_I18N_KEYS[uiStatus]}`) : uiStatus;
                const statusCss = statusClass[uiStatus] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
                const cardToneCss =
                  statusCardToneClass[uiStatus] ||
                  'border-slate-200/70 bg-white/95 dark:border-gray-700/70 dark:bg-gray-900/60';
                return (
                  <article
                    key={p.id}
                    className={`flex h-full flex-col rounded-xl border p-4 shadow-sm ${cardToneCss}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard.writeText(String(p.id));
                          }}
                          className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-base font-semibold text-blue-700 transition hover:scale-[1.03] hover:border-blue-300 dark:border-blue-800/70 dark:bg-blue-950/30 dark:text-blue-300"
                          title={t('requests.copyIdTitle')}
                        >
                          {p.id}
                        </button>
                        {p.order_id ? (
                          <button
                            type="button"
                            onClick={() => {
                              void navigator.clipboard.writeText(p.order_id || '');
                            }}
                            className="rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-600 transition hover:scale-[1.03] hover:border-sky-300 dark:border-sky-800/70 dark:bg-sky-950/30 dark:text-sky-300"
                            title={t('requests.copyOrderTitle')}
                          >
                            {p.order_id}
                          </button>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50/80 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-800/70 dark:bg-indigo-950/30 dark:text-indigo-300">
                          {counterpartyRate && counterpartyRate > 0
                            ? `Курс: ${formatRate2(counterpartyRate)}`
                            : 'Курс: —'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${statusCss}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
                      <div className="rounded-lg border border-rose-200/70 bg-rose-50/60 px-3 py-2 text-left dark:border-rose-900/50 dark:bg-rose-950/20">
                        <div className="text-xs text-gray-500 dark:text-gray-400">Сумма VND</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {Number(p.amount_vnd || 0).toLocaleString('ru-RU')}
                        </div>
                      </div>
                      <div
                        className="rounded-lg border border-emerald-200/70 bg-emerald-50/60 px-3 py-2 text-left dark:border-emerald-900/50 dark:bg-emerald-950/20"
                      >
                        <div className="text-xs text-emerald-700/80 dark:text-emerald-300/80">USDT</div>
                        <HoverTooltip text={amountUSDTFullTooltip}>
                          <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                            {amountUSDT != null ? formatUSDT2(amountUSDT) : '—'}
                          </div>
                        </HoverTooltip>
                      </div>
                      <div className="rounded-lg bg-gray-50/80 px-3 py-2 text-left dark:bg-gray-800/70">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t('requests.created')}</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                              {formatCreatedDateTime(p.created_at || '', t('common.notAvailable'))}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">В работе</div>
                            <div className={`text-xs font-medium ${wt.color}`}>{wt.text}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 rounded-lg bg-gray-50/80 px-3 py-2 dark:bg-gray-800/70">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {isOpenPool ? (
                          <button
                            type="button"
                            disabled={busyID === p.id || lockedPayoutIds.has(p.id)}
                            onClick={() => setTakeConfirmID(p.id)}
                            className="sm:col-span-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {busyID === p.id ? 'Сохранение…' : 'Взять в работу'}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={!qr}
                              onClick={() => {
                                if (!qr) return;
                                setQrModalID(p.id);
                                setQrPreviewLoadFailed(false);
                              }}
                              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-35"
                            >
                              QR
                            </button>
                            {isActive && assignedToMe ? (
                              <button
                                type="button"
                                disabled={busyID === p.id || lockedPayoutIds.has(p.id)}
                                onClick={() => setConfirmID(p.id)}
                                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-35"
                              >
                                {busyID === p.id ? 'Сохранение…' : 'Оплатил'}
                              </button>
                            ) : (
                              <div />
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {!isOpenPool && bankDetailsEntries.length > 0 ? (
                      <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                        {bankDetailsEntries.map((entry) => (
                          <div key={entry.label} className="rounded-lg bg-gray-50/80 px-3 py-2 dark:bg-gray-800/70">
                            <div className="text-xs text-gray-500 dark:text-gray-400">{entry.label}</div>
                            <div className={`${entry.mono ? 'font-mono' : 'font-medium'} text-gray-900 dark:text-gray-100`}>
                              {entry.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <PayoutCompleteConfirmWidget
        open={confirmID != null}
        payoutId={current?.id ?? null}
        amountVnd={current?.amount_vnd ?? null}
        busy={busyID != null}
        onClose={() => {
          if (busyID == null) setConfirmID(null);
        }}
        onConfirm={completeConfirmed}
      />
      <PayoutTakeConfirmWidget
        isOpen={takeConfirmID != null}
        payoutId={takeConfirmID}
        amountVnd={takeCurrent?.amount_vnd ?? null}
        onClose={() => {
          if (busyID == null) setTakeConfirmID(null);
        }}
        onConfirm={async () => {
          if (!takeConfirmID) return;
          await takePayout(takeConfirmID);
        }}
      />
      {qrModalPayout ? (
        <div className="fixed inset-0 z-[230] bg-black/45" onClick={() => setQrModalID(null)}>
          <div
            className="fixed left-1/2 top-1/2 z-[231] w-[min(96vw,52rem)] max-h-[min(92vh,900px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-gray-700 dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setQrModalID(null)}
                className="rounded-lg border border-slate-200 px-2.5 py-1 text-sm text-slate-600 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                title="Закрыть"
              >
                Закрыть
              </button>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                {(() => {
                  const qr = qrModalValue;
                  const isUrl = /^https?:\/\//i.test(qr);
                  const isImage = qr.startsWith('data:image/') || isUrl;
                  const isData = qr.startsWith('data:');

                  if (isImage && qrPreviewLoading) {
                    return (
                      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-800">
                        <span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                      </div>
                    );
                  }
                  if (isImage && !qrPreviewLoadFailed && qrPreviewImageUrl) {
                    return (
                      <img
                        src={qrPreviewImageUrl}
                        alt="QR"
                        className="max-h-[min(70vh,520px)] w-full rounded-lg border border-slate-200 object-contain dark:border-gray-700"
                        onError={() => setQrPreviewLoadFailed(true)}
                      />
                    );
                  }
                  if (isData) {
                    return (
                      <a
                        href={qr}
                        download="qr-file"
                        className="inline-flex rounded-lg border border-indigo-600 bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      >
                        Скачать QR-файл
                      </a>
                    );
                  }
                  if (isUrl) {
                    return (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-4 text-center text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300">
                        Не удалось показать изображение
                      </div>
                    );
                  }
                  return (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      Нет изображения QR
                    </div>
                  );
                })()}
              </div>
              <aside className="w-full shrink-0 space-y-3 border-slate-200 dark:border-gray-700 sm:w-56 sm:border-l sm:pl-4">
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Сумма</div>
                  <div className="mt-0.5 text-base font-semibold tabular-nums text-slate-900 dark:text-gray-100">
                    {Number(qrModalPayout.amount_vnd || 0).toLocaleString('ru-RU')} VND
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Создано</div>
                  <div className="mt-0.5 text-sm font-medium tabular-nums text-slate-900 dark:text-gray-100">
                    {formatCreatedDateTime(qrModalPayout.created_at || '', t('common.notAvailable'))}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Order ID</div>
                  <button
                    type="button"
                    title="Копировать"
                    onClick={() => void navigator.clipboard.writeText(qrModalPayout.order_id || '')}
                    className="mt-0.5 w-full break-all text-left font-mono text-sm font-semibold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-200"
                  >
                    {qrModalPayout.order_id || '—'}
                  </button>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Статус</div>
                  <div className="mt-1">
                    {(() => {
                      const uiStatus =
                        qrModalPayout.status === 'completed'
                          ? 'closed'
                          : qrModalPayout.status === 'problem'
                            ? 'pending_canceled'
                            : qrModalPayout.status === 'in_progress'
                              ? 'work'
                              : qrModalPayout.status;
                      const css =
                        statusClass[uiStatus] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
                      const key = STATUS_I18N_KEYS[uiStatus];
                      return (
                        <span
                          className={`inline-flex min-h-[1.75rem] max-w-full items-center rounded-xl px-2.5 py-1 text-xs font-semibold leading-tight ${css}`}
                        >
                          {key ? t(`requests.${key}`) : qrModalPayout.status || '—'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const isActive =
                    qrModalPayout.status === 'pending' ||
                    qrModalPayout.status === 'in_progress' ||
                    qrModalPayout.status === 'problem';
                  const assignedToMe =
                    typeof qrModalPayout.status_actor_id === 'number' && qrModalPayout.status_actor_id > 0;
                  const canPay = isActive && assignedToMe;
                  if (!canPay) return null;
                  return (
                    <button
                      type="button"
                      disabled={busyID === qrModalPayout.id || lockedPayoutIds.has(qrModalPayout.id)}
                      onClick={() => {
                        setConfirmID(qrModalPayout.id);
                      }}
                      className="mt-1 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-35"
                    >
                      {busyID === qrModalPayout.id ? 'Сохранение…' : 'Оплатил'}
                    </button>
                  );
                })()}
              </aside>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
